import { IAddress } from './address';
import { ICostCenter, IVatRate } from './common';
import { IMatter } from './matter';
import { IMatterParties } from './matterParties';
import { IPrincipalCompany } from './principalCompany';
import { IPrincipalPrivatePerson } from './principalPrivatePerson';
import { ITransaction } from './transaction';
import { IBusinessUnit, IUser } from './user';

export interface IPreliminaryInvoice {
  id: string;
  status: PreliminaryInvoiceStatus;
  matterId: string;
  privatePersonId?: string;
  companyId?: string;
  businessUnitId: string;
  assignToUserId?: string;
  invoiceUserId?: string;
  entryDate: string;
  periodStartDate: string;
  periodEndDate: string;
  acceptDate?: Date;
  invoicedDate?: Date;
  reviewDate?: string;
  printFreeText?: boolean;
  freeText?: string;
  changePercent?: number;
  grossFeeChanged?: boolean;
  grossFee?: number;
  grossFeeInCurrency?: number;
  grossFeeCalculationVatRateId?: string;
  isTotalAmountRoundingActive?: boolean;
  rejectedByUserId?: string;
  rejectedNetSum: number;
  rejectionReason?: string;
  rejectionDate?: Date;
  costCenterId?: string;
  ownerUserId?: string;
  assistantUserId?: string;
  matter: IMatter;
  company?: IPrincipalCompany;
  privatePerson?: IPrincipalPrivatePerson;
  businessUnit?: IBusinessUnit;
  assignToUser?: IUser;
  invoiceUser?: IUser;
  grossFeeCalculationVatRate?: IVatRate;
  rejectedByUser?: IUser;
  costCenter?: ICostCenter;
  ownerUser?: IUser;
  assistantUser?: IUser;
  payers?: IPreliminaryInvoicePayer[];
  reviewers?: IPreliminaryInvoiceReviewer[];
  transactions?: ITransaction[];
  hasImportantNotes?: boolean;
  hasImportantRelatedNotes?: boolean;
  hasNotes?: boolean;
  sumOfBillableEntries: number;
  sumOfBillableEntriesInCurrency: number;
  reviewStatus: ReviewStatus;
  version: string;
}
// Preliminary Invoice V2
export interface IPreliminaryInvoiceV2 {
  status: PreliminaryInvoiceStatus;
  matterId: string;
  privatePersonId?: string;
  companyId?: string;
  assignToUserId?: string;
  entryDate: string;
  periodStartDate: string;
  periodEndDate: string;
  reviewStatus: ReviewStatus;
  matter: IMatter;
  company?: IPrincipalCompany;
  privatePerson?: IPrincipalPrivatePerson;
  hasNotes?: boolean;
  hasImportantNotes?: boolean;
  hasImportantRelatedNotes?: boolean;
  totalNetAmount: number;
  totalNetAmountInMatterCurrency: number;
}
export type AnyPreliminaryInvoice = IPreliminaryInvoice | IPreliminaryInvoiceV2;
export enum PreliminaryInvoiceStatus {
  OPEN = 'open',
  REVIEWED = 'reviewed',
  UPLIFT_DOWNLIFT = 'uplift-downlift',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  INVOICED = 'invoiced',
}
export interface IPreliminaryInvoicePayer {
  id: string;
  status: PreliminaryInvoiceStatus;
  preliminaryInvoiceId: string;
  matterPartyId: string;
  companyId?: string;
  privatePersonId?: string;
  contactId?: string;
  addressId?: string;
  vatRateId?: string;
  taxabilityId: string;
  paymentTermId?: string;
  noticePeriodId?: string;
  currency: string;
  language?: string;
  notInvoiced?: boolean;
  yourReference?: string;
  sharePercentage: number;
  orgSharePercentage: number;
  netShare: number;
  orgNetShare: number;
  netFee: number;
  orgNetFee: number;
  netCost?: number;
  netShareInCurrency?: number;
  orgNetShareInCurrency?: number;
  netFeeInCurrency?: number;
  orgNetFeeInCurrency?: number;
  useDiscountTable?: boolean;
  concernDiscount?: boolean;
  discount?: number;
  discountInCurrency?: number;
  discountPercentage?: number;
  changedDiscountSum?: boolean;
  sumOfOpen?: number;
  sumOfAccepted?: number;
  sumOfInvoiced?: number;
  useAdvance?: boolean;
  usedAssignmentAdvance?: number;
  usedPayerAdvance?: number;
  incuranceCompanyId?: string;
  legalInsuranceNumber?: string;
  insuranceMaximum?: number;
  deductiblePercent?: number;
  deductibleMinimum?: number;
  createInvoiceForDeductible?: boolean;
  invoiceTemplateMasterId?: string;
  invoiceTemplateSpecificationId?: string;
  showDiscountPercentage?: boolean;
  hideExpensesFromMasterPage?: boolean;
  showTransferOrder?: boolean;
  showSpecificationPage?: boolean;
  showAuthorSpecificationPage?: boolean;
  showRowsWithZeroTotalInSpecification?: boolean;
  hideExpensesFromSpecification?: boolean;
  groupTransactionsByTypeInSpecification?: boolean;
  groupTransactionsByPhaseInSpecification?: boolean;
  showRowsInPhaseGroupsInSpecification?: boolean;
  groupExpensesByTypeInSpecification?: boolean;
  groupExpensesByPhaseInSpecification?: boolean;
  showTransactionAuthorPerRowInSpecification?: boolean;
  showTransactionHourPricePerRowInSpecification?: boolean;
  showTransactionHoursPerRowInSpecification?: boolean;
  showTransactionAmountPerRowInSpecification?: boolean;
  showExpenseUnitsPerRowInSpecification?: boolean;
  showExpenseAmountPerRowInSpecification?: boolean;
  showTotalTransactionUnitsInSpecification?: boolean;
  showTotalExpenseUnitsInSpecification?: boolean;
  preliminaryInvoice?: IPreliminaryInvoice;
  matterParty?: IMatterParties;
  company?: IPrincipalCompany;
  privatePerson?: IPrincipalPrivatePerson;
  contact?: IPrincipalPrivatePerson;
  address?: IAddress;
  vatRate?: IVatRate;
}
export interface IPreliminaryInvoiceReviewer {
  status: string;
  statusCode: ReviewerStatusCode;
  preliminaryInvoiceId: string;
  sequenceNumber: number;
  reviewerUserId?: string;
  statusUpdatedByUserId?: string;
  reviewDate?: Date;
  skipDate?: Date;
  preliminaryInvoice?: IPreliminaryInvoice;
  reviewerUser?: IUser;
  statusUpdatedByUser?: IUser;
}
export enum ReviewerStatusCode {
  WAITING = 'waiting',
  REVIEWED = 'reviewed',
  SKIPPED = 'skipped',
}
export enum ReviewStatus {
  IN_REVIEW = 'in-review',
  REVIEW_NOT_ENABLED = 'review-not-enabled',
  REVIEWED = 'reviewed',
}
export enum MyInvoicingTab {
  billableMatters = 'billableMatters',
  preliminaryInvoices = 'preliminaryInvoices',
}

export enum MyInvoicingDetailTab {
  general = 'general',
  billableEntries = 'billableEntries',
  payers = 'payers',
  notes = 'notes',
}

export enum PreliminaryInvoiceCreationType {
  PERIODICAL = 'periodical',
  UNTIL_DATE = 'until-date',
}

export type PreliminaryInvoiceCreationRequest =
  | { type: PreliminaryInvoiceCreationType.PERIODICAL }
  | {
      type: PreliminaryInvoiceCreationType.UNTIL_DATE;
      entryDate?: string;
      periodEndDate?: string;
    };
